import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { BottomButtonsLayout, theme } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import AccountContext from 'context/AccountContext';
import useAfterVehicleRedirect from 'hooks/useAfterVehicleRedirect';
import useUpdatePermit from 'hooks/useUpdatePermit';
import { isEqualWith } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import AccessPermitTicket from 'screens/permit/access/AccessPermitTicket';
import PermitBottomButtons from 'screens/permit/access/PermitBottomButtons';
import LoyaltyPermitTicket from 'screens/permit/loyalty/LoyaltyPermitTicket';
import ModalContentWrapper from 'screens/permit/ModalContentWrapper';
import { usePermitStore } from 'stores/permitStore';
import { useVehicleStore } from 'stores/vehicleStore';
import { ColumnWithMargins } from 'styles/ContainerStyles';
import { LicensePlate, UnifiedPermit } from 'types/generatedSchemaTypes';
import { RootNavProps, RootStackParamList } from 'types/navigation';
import { showLicensePlate } from 'utils/permitUtils';
import SalesOrderTicket from './salesOrder/SalesOrderTicket';
import { useUiStore } from 'stores/uiStore';
const TicketComponent = ({ permit }: { permit: UnifiedPermit }) => {
  switch (permit.permitType) {
    case 'LOYALTY':
      return <LoyaltyPermitTicket permit={permit} extended />;
    case 'ACCESS':
    case 'ACCESS_TO_MEMBER_GROUP_LTP':
    case 'DIGITAL_PERMIT':
      return <AccessPermitTicket permit={permit} />;
    case 'SALES_ORDER':
      return <SalesOrderTicket salesOrder={permit} />;
    default:
      return <></>;
  }
};

const Permit: React.FC = () => {
  const { t } = useTranslation();
  const { goBack, addListener, navigate } = useNavigation<RootNavProps>();
  const {
    params: { permitId },
  } = useRoute<RouteProp<RootStackParamList, 'permitRedeem'>>();
  const {
    customer: { permits },
  } = useContext(AccountContext);
  const permit = permits?.find((p) => p?.id === permitId);
  const isPermitLoyaltyType = permit?.permitType === 'LOYALTY';
  const selectedLicensePlates =
    usePermitStore.getState().selectedLicensePlatesPermitRedeem;
  const [loadingComponent, setLoadingComponent] = React.useState(true);
  const { navigateAfterVehicleSaved } = useAfterVehicleRedirect();
  const [isDirty, _setIsDirty] = React.useState(false);
  const { isDiscarded, vehicleSaved, resetVehicleStore, setVehicleSaved } =
    useVehicleStore((state) => state);

  const isDirtyRef = useRef(isDirty);
  const setIsDirty = (value: boolean) => {
    isDirtyRef.current = value;
    _setIsDirty(value);
  };

  const isRedeemable =
    permit?.permitType !== 'SALES_ORDER' || showLicensePlate(permit);
  const currentRegistrationPlates = useRef(
    permit?.registrationPlates?.length || 0
  );

  const { updatePermit, loading } = useUpdatePermit(permitId, {
    isRedeemable,
    isPermitLoyaltyType,
    currentRegistrationPlates,
  });

  const hasUpdatedVehicle = useRef(false);

  const handleRedeem = useCallback(async () => {
    if (selectedLicensePlates.length === 0) {
      return;
    }
    await updatePermit({
      variables: {
        input: {
          permitId,
          permitActionType: 'PATCH',
          registrationPlates: selectedLicensePlates,
        },
      },
    });
  }, [updatePermit, permitId, selectedLicensePlates]);

  // These handle the changes made in the edit vehicle discard modal and should not be removed for now
  useEffect(() => {
    if (vehicleSaved && !hasUpdatedVehicle.current) {
      hasUpdatedVehicle.current = true;
      handleRedeem();
      setVehicleSaved(false);
      useUiStore.getState().setSheetModal('');
      setIsDirty(false);
      navigateAfterVehicleSaved();
    }
  }, [vehicleSaved, navigateAfterVehicleSaved, handleRedeem, setVehicleSaved]);

  useEffect(() => {
    if (isDiscarded) {
      useUiStore.getState().setSheetModal('');
      resetVehicleStore();
      setIsDirty(false);
      navigateAfterVehicleSaved();
    }
  }, [isDiscarded, navigateAfterVehicleSaved, resetVehicleStore]);

  const navigation = useNavigation();

  const currentLicensePlates = usePermitStore(
    (state) => state.selectedLicensePlatesPermitRedeem
  );
  const originalRegistrationPlates: LicensePlate[] = useMemo(() => {
    return (permit?.registrationPlates ?? []).map((plate) => {
      return {
        plateText: plate?.plateText,
        countryCode: plate?.countryCode,
      } as LicensePlate;
    });
  }, [permit?.registrationPlates]);

  useEffect(() => {
    addListener('beforeRemove', handleNavigation);
    return () => {
      addListener('beforeRemove', handleNavigation);
    };
  }, [addListener, navigate]);

  useEffect(() => {
    if (permit?.usageType !== 'CUSTOMER') {
      setIsDirty(
        !isEqualWith(originalRegistrationPlates, currentLicensePlates)
      );
    }
  }, [permit?.usageType, originalRegistrationPlates, currentLicensePlates]);

  const headerTitle = useCallback(() => {
    if (permit?.usageType === 'CUSTOMER' || permit?.usageType === 'SKIDATA') {
      return t('services.permit');
    }
    return permit?.isWaitingActivation
      ? t('permit.access.redeemPermit')
      : t('permit.access.updatePermit');
  }, [permit?.usageType, permit?.isWaitingActivation, t]);

  useEffect(() => {
    if (
      permit?.permitType === 'ACCESS' ||
      permit?.permitType === 'ACCESS_TO_MEMBER_GROUP_LTP' ||
      permit?.permitType === 'DIGITAL_PERMIT'
    ) {
      navigation.setOptions({
        title: headerTitle(),
        cardStyle: {
          backgroundColor: theme.colors.white,
        },
      });
    }
    if (isPermitLoyaltyType) {
      navigation.setOptions({
        title:
          permit?.usageType === 'CUSTOMER'
            ? t('permit.loyalty.label')
            : t('permit.loyalty.detailsTitle'),
        cardStyle: { backgroundColor: theme.colors.gray100 },
      });
    }
  }, [
    t,
    navigation,
    permit?.permitType,
    permit?.usageType,
    isPermitLoyaltyType,
    headerTitle,
  ]);

  useEffect(() => {
    // On load, set up the selected license plates of the permit
    usePermitStore
      .getState()
      .setSelectedLicensePlatesPermitRedeem(originalRegistrationPlates);
    setLoadingComponent(false);
  }, [permit?.registrationPlates, originalRegistrationPlates]);

  const handleNavigation = (e: any) => {
    if (isDirtyRef.current) {
      e?.preventDefault();
      useUiStore.getState().setSheetModal('unsavedEditVehicle');
    }
  };

  if (!permit) {
    showToast(t('error.readPermit'), 'error');
    goBack();
    return <></>;
  }

  if (loadingComponent) {
    return <></>;
  }

  return (
    <BottomButtonsLayout
      bottomButtonsBackgroundColor={theme.colors.white}
      contentBottom={
        <ColumnWithMargins
          mr={20}
          ml={20}
          mb={theme.normalising.heightPixel(30)}>
          <PermitBottomButtons
            onPress={handleRedeem}
            isRedeemed={
              !permit.isWaitingActivation || !showLicensePlate(permit)
            }
            isRedeemable={isRedeemable}
            loading={loading}
            disabled={!isDirty || currentLicensePlates.length === 0}
            usageType={permit?.usageType}
            showLicensePlate={showLicensePlate(permit)}
          />
        </ColumnWithMargins>
      }>
      <ModalContentWrapper>
        <TicketComponent permit={permit} />
      </ModalContentWrapper>
    </BottomButtonsLayout>
  );
};

export default Permit;
